/**
 * This is a port of angular 1.3.14 $caacheFactory with functionality for `where` and `all`
 */

import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ModelCache {
  caches = {};

  /**
   * @ngdoc method
   * @name ModelCache#add
   *
   * @description
   * Add a new cache for ModeModels
   *
   * @param {string} cacheId Name or id of for a newly created cache.
   * @returns {ModelCache.Cache} Newly created cache.
   */
  private create(cacheId: string): ModelCache.Cache {
    return (this.caches[cacheId] = new ModelCache.Cache(() => {
      delete this.caches[cacheId];
    }));
  }

  /**
   * @ngdoc method
   * @name ModelCache#get
   *
   * @description
   * Get access to a cache object by the `cacheId` used when it was created.
   *
   * @param {string} cacheId Name or id of a cache to access.
   * @returns {ModelCache.Cache} Cache object identified by the cacheId or undefined if no such cache.
   */
  private get(cacheId: string): ModelCache.Cache {
    return this.caches[cacheId];
  }

  /**
   * @ngdoc method
   * @name ModelCache#getOrCreate
   *
   * @description
   * Get access to a cache object by the `cacheId` used when it was created, or create a new one.
   *
   * @param {string} cacheId Name or id of a cache to access.
   * @returns {ModelCache.Cache} Cache object identified by the cacheId or a new one.
   */
  getOrCreate(cacheId: string): ModelCache.Cache {
    return this.get(cacheId) || this.create(cacheId);
  }
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ModelCache {
  /**
   * @ngdoc type
   * @name ModelCache.Cache
   *
   * @description
   * A cache object used to store and retrieve data, primarily used by
   * {@link $http $http} and the {@link ng.directive:script script} directive to cache
   * templates and other data.
   *
   * ```js
   *  angular.module('superCache')
   *    .factory('superCache', ['modelCache', function(modelCache: ModelCache) {
   *      return modelCache.getOrCreate('super-cache');
   *    }]);
   * ```
   */
  export class Cache {
    private data = {};
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private onDestroy: () => void = () => {};

    constructor(onDestroy: () => void) {
      this.onDestroy = onDestroy;
    }
    /**
     * @ngdoc method
     * @name ModelCache.Cache#put
     * @kind function
     *
     * @description
     * Inserts a named entry into the {@link modelCache.Cache Cache} object to be
     * retrieved later.
     *
     * It will not insert undefined values into the cache.
     *
     * @param {string} key the key under which the cached data is stored.
     * @param {*} value the value to store alongside the key. If it is undefined, the key
     *    will not be stored.
     * @returns {*} the value stored.
     */
    put(key: string, value) {
      if (typeof value === 'undefined') return;
      if (!(key in this.data)) this.data[key] = value;

      return value;
    }

    /**
     * @ngdoc method
     * @name ModelCache.Cache#get
     * @kind function
     *
     * @description
     * Retrieves named data stored in the {@link modelCache.Cache Cache} object.
     *
     * @param {string} key the key of the data to be retrieved
     * @returns {*} the value stored.
     */
    get(key: string) {
      return this.data[key];
    }

    /**
     * @ngdoc method
     * @name ModelCache.Cache#remove
     * @kind function
     *
     * @description
     * Removes an entry from the {@link modelCache.Cache Cache} object.
     *
     * @param {string} key the key of the entry to be removed
     */
    remove(key: string) {
      delete this.data[key];
    }

    /**
     * @ngdoc method
     * @name ModelCache.Cache#destroy
     * @kind function
     *
     * @description
     * Destroys the {@link modelCache.Cache Cache} object entirely,
     * removing it from the {@link modelCache modelCache} set.
     */
    destroy() {
      this.data = null;
      this.onDestroy();
    }

    /**
     * Mode function
     * @ngdoc method
     * @name ModelCache.Cache#all
     * @kind function
     *
     * @description
     * returns the entire cache.
     */
    all() {
      return this.data;
    }

    /**
     * Mode function
     * @ngdoc method
     * @name ModelCache.Cache#where
     * @kind function
     *
     * @description
     * returns an array of elements from the cache that meet the
     * condition function passed.
     */
    where(condition) {
      const result = [];
      for (const key in this.data) {
        const item = this.data[key];
        if (condition.apply(this, item)) {
          result.push(item);
        }
      }
      return result;
    }
  }
}
